function short(v: unknown): string {
  if (typeof v === "string") return cut(v);
  let text: string | undefined;
  try {
    text = safeStringify(v);
  } catch {
    text = undefined;
  }
  if (text === undefined) {
    try {
      text = String(v);
    } catch {
      text = "";
    }
  }
  return cut(text);
}

function safeStringify(v: unknown): string | undefined {
  try {
    const seen = new WeakSet<object>();
    const json = JSON.stringify(v, (_key, val) => {
      if (val && typeof val === "object") {
        if (seen.has(val as object)) return String(val);
        seen.add(val as object);
      }
      if (typeof val === "bigint") return String(val);
      return val;
    });
    return typeof json === "string" ? json : undefined;
  } catch {
    return undefined;
  }
}

function cut(text: string): string {
  let safe = text;
  try {
    safe = typeof text === "string" ? text : String(text);
  } catch {
    safe = "";
  }
  const chars = Array.from(safe);
  return chars.length > 200 ? chars.slice(0, 200).join("") + "…" : safe;
}

function show(v: unknown): string {
  try {
    return short(v);
  } catch {
    try {
      return String(v);
    } catch {
      return "";
    }
  }
}

const SECRET_PATH = /secret|token|passw(or)?d|api[_-]?key|credential|authoriz|bearer|private[_-]?key/i;
const TOKEN_RUN = /[A-Za-z0-9_\-+/=.]{24,}/;
const TOKEN_PREFIX = /^(sk[-_]|ghp_|xox|AKIA|ASIA[A-Z0-9])/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function secretPath(path: string): boolean {
  try {
    return SECRET_PATH.test(path);
  } catch {
    return true;
  }
}

function secretValue(v: unknown): boolean {
  if (typeof v !== "string") return false;
  if (UUID.test(v)) return false;
  if (TOKEN_PREFIX.test(v)) return true;
  return TOKEN_RUN.test(v);
}

function primitiveText(v: unknown): string | undefined {
  if (v === null) return "null";
  if (typeof v === "string") return v;
  if (typeof v === "number") return String(v);
  if (typeof v === "boolean") return String(v);
  return undefined;
}

const SAFE_OP = /^[A-Za-z_-]{1,20}$/;

function safeOp(op: unknown): string {
  return typeof op === "string" && SAFE_OP.test(op) ? op : "?";
}

function hiddenPath(path: unknown, hr: boolean): string {
  if (typeof path !== "string") return String(path);
  const marker = hr ? "[skriveno]" : "[hidden]";
  const shown = path
    .split("/")
    .map((seg) => (secretValue(seg) ? marker : seg))
    .join("/");
  return cut(shown);
}

function sourcePath(e: { path: unknown }): string {
  try {
    return typeof e.path === "string" ? e.path : String(e.path);
  } catch {
    return "";
  }
}

function diffPart(e: { op: string; path: string; value: unknown }, hr: boolean): string {
  const head = `- ${safeOp(e.op)} ${hiddenPath(e.path, hr)}`;
  let v: unknown;
  try {
    v = e.value;
  } catch {
    v = undefined;
  }
  if (v === undefined) return head;
  let text: string | undefined;
  try {
    text = primitiveText(v);
  } catch {
    text = undefined;
  }
  if (text === undefined) return `${head} = ${hr ? "[objekt]" : "[object]"}`;
  if (secretPath(sourcePath(e)) || secretValue(text)) {
    return `${head} = ${hr ? "[skriveno]" : "[hidden]"}`;
  }
  return `${head} = ${cut(show(text))}`;
}

const TARGET_KEYS = ["kind", "type", "id", "name"];

function targetText(target: unknown, hr: boolean): string | undefined {
  if (!target || typeof target !== "object" || Array.isArray(target)) return undefined;
  const rec = target as Record<string, unknown>;
  const pairs: string[] = [];
  for (const key of TARGET_KEYS) {
    let v: unknown;
    try {
      v = rec[key];
    } catch {
      v = undefined;
    }
    let text: string | undefined;
    try {
      text = primitiveText(v);
    } catch {
      text = undefined;
    }
    if (text === undefined) continue;
    const shown = secretValue(text) ? (hr ? "[skriveno]" : "[hidden]") : cut(text);
    pairs.push(`${key}=${shown}`);
  }
  return pairs.length ? pairs.join(", ") : undefined;
}

function diffEntries(diff: unknown): Array<{ op: string; path: string; value: unknown }> {
  const out: Array<{ op: string; path: string; value: unknown }> = [];
  if (!diff || typeof diff !== "object") return out;
  let list: unknown[];
  try {
    list = Array.isArray(diff) ? diff : Object.values(diff as Record<string, unknown>);
  } catch {
    return out;
  }
  for (const entry of list) {
    if (!entry || typeof entry !== "object" || Array.isArray(entry)) continue;
    const rec = entry as Record<string, unknown>;
    let op = "";
    let path = "";
    try {
      op = rec.op === undefined || rec.op === null ? "" : String(rec.op);
      path = rec.path === undefined || rec.path === null ? "" : String(rec.path);
    } catch {
      continue;
    }
    let value: unknown;
    try {
      value = rec.value;
    } catch {
      value = undefined;
    }
    out.push({ op, path, value });
  }
  return out;
}

export function buildDecisionDraft(d: any, lang: "hr" | "en"): string {
  const hr = lang !== "en";
  let src: Record<string, any>;
  try {
    src = d && typeof d === "object" ? (d as Record<string, any>) : {};
  } catch {
    src = {};
  }
  const field = (key: string): string => {
    let v: unknown;
    try {
      v = src[key];
    } catch {
      v = undefined;
    }
    if (v === undefined || v === null || v === "") return "?";
    let text: string | undefined;
    try {
      text = primitiveText(v);
    } catch {
      text = undefined;
    }
    if (text === undefined) return hr ? "[objekt]" : "[object]";
    if (secretValue(text)) return hr ? "[skriveno]" : "[hidden]";
    return cut(show(text));
  };
  const parts: string[] = [];
  parts.push(
    hr
      ? `Želim razraditi odluku ${field("id")} (${field("action_type")}, rizik ${field("risk")}, status ${field("status")}).`
      : `I want to work through decision ${field("id")} (${field("action_type")}, risk ${field("risk")}, status ${field("status")}).`,
  );
  let target: unknown;
  try {
    target = src.target_ref;
  } catch {
    target = undefined;
  }
  const targetLine = targetText(target, hr);
  if (targetLine) {
    parts.push(`${hr ? "Cilj" : "Target"}: ${targetLine}`);
  }
  let diff: unknown;
  try {
    diff = src.diff;
  } catch {
    diff = undefined;
  }
  let entries: Array<{ op: string; path: string; value: unknown }> = [];
  try {
    entries = diffEntries(diff);
  } catch {
    entries = [];
  }
  if (entries.length) {
    parts.push(hr ? "Predložena promjena:" : "Proposed change:");
    for (const e of entries) {
      try {
        parts.push(diffPart(e, hr));
      } catch {
        parts.push(`- ${safeOp(e.op)} ${hiddenPath(e.path, hr)}`);
      }
    }
  }
  const tail = hr
    ? "Koje su opcije i rizici, što preporučuješ? Ne odobravam ništa dok se ne dogovorimo."
    : "What are the options and risks, and what do you recommend? I am not approving anything until we agree.";
  parts.push(tail);
  const out = parts.filter((p) => p.length > 0).join("\n");
  const budget = 5000 - Array.from("…\n").length - Array.from(tail).length;
  const chars = Array.from(out);
  if (chars.length > 5000) {
    return chars.slice(0, Math.max(0, budget)).join("") + "…\n" + tail;
  }
  return out;
}
